import { AxiosService } from 'src/axios/axios.service';
import { tAxiosCustomConfig } from 'src/axios/axios.types';
import { rejectGatewayErrorBody } from 'src/axios/gatewayErrorBody';
import type {
  tChangeAnswer,
  tCloseEditBody,
  tCloseFeed,
  tCloseRegisterBody,
  tCloseSettings,
  tCloseSummary,
  tCloseType,
  tRegisterAnswer,
  tStageOpensOutcome,
} from './LeadClose.types';

type tEnvelope<T> = { success: boolean; code: number; data: T };

/**
 * leads-service labels its close events with the calling client (LEA-50).
 * Scoped to LeadClose; the app-wide header is GRO-1214.
 */
const LEAD_CLOSE_CLIENT_HEADERS = { 'X-Leadsales-Client': 'web' };

/**
 * A refusal (403, 404, 409, 422, 429) is an answer the form explains, not an
 * incident, so none goes to Sentry.
 */
function config(
  options: { idempotencyKey?: string; params?: object } = {},
): tAxiosCustomConfig {
  return {
    skipSentryCapture: true,
    ...(options.params && { params: options.params }),
    headers: {
      ...LEAD_CLOSE_CLIENT_HEADERS,
      ...(options.idempotencyKey && {
        'Idempotency-Key': options.idempotencyKey,
      }),
    },
  };
}

/**
 * The envelope's data. Behind production's gateway a 5xx can arrive as a 200
 * with the gateway's own body: it is thrown as the error it is.
 */
function dataOf<T>(envelope: tEnvelope<T>): T {
  const body = rejectGatewayErrorBody(envelope);
  // A 200 that is not the envelope (an HTML fallback page, an empty body) is
  // no answer: thrown, so it is never merged as a saved result.
  if (body?.success !== true || body.data == null) {
    throw new Error('lead-close: answer is not the bff-web envelope');
  }
  return body.data;
}

function leadBase(workspaceId: string, leadId: string): string {
  return `/workspace/${workspaceId}/leads/${leadId}`;
}

/**
 * LEA-50 on bff-web, which relays to leads-service. The create carries an
 * Idempotency-Key: replaying the same key returns the same result, which is
 * how a lost answer is recovered without registering the sale twice.
 */
export const LeadCloseService = {
  async getSettings(workspaceId: string): Promise<tCloseSettings> {
    return dataOf(
      await AxiosService.get<tEnvelope<tCloseSettings>>(
        `/workspace/${workspaceId}/close-settings`,
        config(),
      ),
    );
  },

  async patchSettings(
    workspaceId: string,
    body: object,
  ): Promise<tCloseSettings> {
    return dataOf(
      await AxiosService.patch<object, tEnvelope<tCloseSettings>>(
        `/workspace/${workspaceId}/close-settings`,
        body,
        config(),
      ),
    );
  },

  async getSummary(
    workspaceId: string,
    leadId: string,
  ): Promise<tCloseSummary> {
    return dataOf(
      await AxiosService.get<tEnvelope<tCloseSummary>>(
        `${leadBase(workspaceId, leadId)}/close-events`,
        config(),
      ),
    );
  },

  async register(
    workspaceId: string,
    leadId: string,
    body: tCloseRegisterBody,
    idempotencyKey: string,
  ): Promise<tRegisterAnswer> {
    return dataOf(
      await AxiosService.post<tCloseRegisterBody, tEnvelope<tRegisterAnswer>>(
        `${leadBase(workspaceId, leadId)}/close-events`,
        body,
        config({ idempotencyKey }),
      ),
    );
  },

  async edit(
    workspaceId: string,
    leadId: string,
    resultId: string,
    body: tCloseEditBody,
  ): Promise<tChangeAnswer> {
    return dataOf(
      await AxiosService.patch<tCloseEditBody, tEnvelope<tChangeAnswer>>(
        `${leadBase(workspaceId, leadId)}/close-events/${resultId}`,
        body,
        config(),
      ),
    );
  },

  async remove(
    workspaceId: string,
    leadId: string,
    resultId: string,
    revision: number,
  ): Promise<tChangeAnswer> {
    return dataOf(
      await AxiosService.delete<tEnvelope<tChangeAnswer>>(
        `${leadBase(workspaceId, leadId)}/close-events/${resultId}`,
        config({ params: { revision } }),
      ),
    );
  },

  async feed(
    workspaceId: string,
    leadId: string,
    cursor?: string,
  ): Promise<tCloseFeed> {
    return dataOf(
      await AxiosService.get<tEnvelope<tCloseFeed>>(
        `${leadBase(workspaceId, leadId)}/history/feed`,
        config(cursor ? { params: { cursor } } : {}),
      ),
    );
  },

  async setStageOpensOutcome(
    workspaceId: string,
    funnelId: string,
    stageId: string,
    value: tCloseType | null,
  ): Promise<tStageOpensOutcome> {
    return dataOf(
      await AxiosService.put<
        { opens_outcome: tCloseType | null },
        tEnvelope<tStageOpensOutcome>
      >(
        `/workspace/${workspaceId}/funnel/${funnelId}/stage/${stageId}/opens-outcome`,
        { opens_outcome: value },
        config(),
      ),
    );
  },
};
